import { api } from './api';

/** docs/plans/LANGUAGE-REQUESTS.md §6. The catalog comes from the server, never a TS copy. */
export type LanguageStatus =
  | 'gathering'
  | 'implementing'
  | 'in_review'
  | 'live'
  | 'declined'
  | 'paused';

export interface CatalogLanguage {
  code: string;
  native: string;
  english: string;
}

export interface LanguageRow {
  code: string;
  status: LanguageStatus;
  counted: number;
  public_reason: string | null;
  doc_url: string | null;
  mine: boolean;
  can_review: boolean;
}

export interface LanguagesModel {
  threshold: number;
  max_picks: number;
  /** Picks holding a slot, counted by the server's own rule (L8); never recomputed here. */
  active_picks: number;
  verified: boolean;
  catalog: CatalogLanguage[];
  languages: LanguageRow[];
  brought: number;
  share_code: string | null;
}

export interface PublicLanguage {
  code: string;
  name_native: string;
  name_en: string;
  status: LanguageStatus;
  public_reason: string | null;
  counted: number;
  threshold: number;
}

export type LanguageRequestSource = 'page' | 'share_link' | 'support_chat';

export async function getLanguages(): Promise<LanguagesModel> {
  const response = await api.get<LanguagesModel>('/languages');
  return response.data;
}

export async function addLanguageRequest(body: {
  code: string;
  can_review?: boolean;
  ref?: string | null;
  source: LanguageRequestSource;
}): Promise<void> {
  await api.post('/languages/requests', body);
}

export async function setLanguageCanReview(code: string, canReview: boolean): Promise<void> {
  await api.patch(`/languages/requests/${encodeURIComponent(code)}`, { can_review: canReview });
}

export async function removeLanguageRequest(code: string): Promise<void> {
  await api.delete(`/languages/requests/${encodeURIComponent(code)}`);
}

export async function getLanguageShareCode(): Promise<string> {
  const response = await api.post<{ share_code: string }>('/languages/share-code');
  return response.data.share_code;
}

export async function getPublicLanguage(code: string): Promise<PublicLanguage> {
  const response = await api.get<PublicLanguage>(`/languages/public/${encodeURIComponent(code)}`);
  return response.data;
}

/** The error key of a refused write (409 slots_full / already_live / declined, 422 unknown_language). */
export function languageErrorKey(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === 'string' ? data.error : null;
}
